import type { ServiceBillingProposal } from '@forq/service-billing-data';
import type { TenantCompanyProfile } from '@forq/platform-data';

/**
 * Service-Belege als druckfertiges HTML im FTW-Layout, gerendert aus einem Rechnungsvorschlag:
 *   - Kostenvoranschlag: die priced Positionen als freibleibendes Angebot vor der Arbeit
 *     (mit Gültigkeit + Kundenfreigabe-Unterschrift), Grundlage für die spätere Freigabe.
 *   - Rechnungsvorschlag: dieselben Positionen als kaufmännischer Beleg zur Rechnungsprüfung.
 * Gleicher Briefkopf/Fußzeile wie die Vorgangs- und Miet-Belege (aus dem Firmenprofil). Gedruckt /
 * als PDF über den Browser (Button im erzeugten HTML). DATEN-getrieben, kein hinterlegtes PDF.
 */

export type ServiceDocumentKind = 'estimate' | 'billing';

export const serviceDocumentLabels: Readonly<Record<ServiceDocumentKind, string>> = {
  estimate: 'Kostenvoranschlag',
  billing: 'Rechnungsvorschlag',
};

const documentHeading: Readonly<Record<ServiceDocumentKind, string>> = {
  estimate: 'KOSTENVORANSCHLAG',
  billing: 'RECHNUNGSVORSCHLAG',
};

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const qtyFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });
function money(value: number): string { return euro.format(value); }
function qty(value: number): string { return qtyFormat.format(value); }
function dateLabel(value: string): string {
  const [year, month, day] = (value || '').slice(0, 10).split('-');
  return year && month && day ? `${day}.${month}.${year}` : (value || '–');
}
function esc(value: string): string {
  return (value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

function letterhead(company: TenantCompanyProfile): string {
  const { identity, letterhead: lh } = company;
  const slotLeft = lh.logoDataUrl
    ? `<img src="${esc(lh.logoDataUrl)}" alt="${esc(identity.name)}">`
    : `<div class="logo-name">${esc(identity.name || 'FORQ')}</div>`;
  const slotCenter = lh.logoCenterDataUrl ? `<img src="${esc(lh.logoCenterDataUrl)}" alt="">` : '';
  const slotRight = lh.logoRightDataUrl
    ? `<img src="${esc(lh.logoRightDataUrl)}" alt="">`
    : `<div class="services">Handel&nbsp;&nbsp;Service<br>Ersatzteile&nbsp;&nbsp;Vermietung<br><span class="red">für Flurförderzeuge</span></div>`;
  return `<div class="hd"><div class="hd-slot hd-left">${slotLeft}</div><div class="hd-slot hd-center">${slotCenter}</div><div class="hd-slot hd-right">${slotRight}</div></div><hr class="hd-rule">`;
}

function footer(company: TenantCompanyProfile): string {
  const { identity, contact, bank } = company;
  const col = (heading: string, lines: (string | false)[]) =>
    `<div><span class="fh">${esc(heading)}</span>${lines.filter(Boolean).map((l) => esc(l as string)).join('\n')}</div>`;
  return `<div class="footer">
    ${col(identity.name || 'Firma', [identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()])}
    ${col('Kontakt', [contact.phone && `Tel.: ${contact.phone}`, contact.fax && `Fax: ${contact.fax}`, contact.email, contact.website])}
    ${col('Bankverbindung', [bank.bankName, bank.iban && `IBAN: ${bank.iban}`, bank.bic && `BIC: ${bank.bic}`])}
    ${col('Unternehmen', [identity.managingDirectors && `Geschäftsführer: ${identity.managingDirectors}`, identity.registerCourt && identity.register && `${identity.registerCourt} ${identity.register}`, identity.vatId && `USt-IdNr.: ${identity.vatId}`])}
  </div>`;
}

function positionsTable(proposal: ServiceBillingProposal): string {
  const rows = proposal.lines.map((line, index) => `<tr>
      <td class="c">${index + 1}</td>
      <td><strong>${esc(line.description)}</strong>${line.sourceNumber ? `<div class="postext">${esc(line.sourceNumber)}</div>` : ''}</td>
      <td class="num">${line.kind === 'text' ? '' : qty(line.quantity)}</td>
      <td>${esc(line.unit)}</td>
      <td class="num">${line.kind === 'text' ? '' : money(line.unitPrice)}</td>
      <td class="num">${line.discountPercent > 0 ? `${qty(line.discountPercent)} %` : ''}</td>
      <td class="num">${line.kind === 'text' ? '' : money(line.netAmount)}</td>
    </tr>`).join('');
  return `<table class="pos">
    <thead><tr><th class="c">Pos.</th><th>Bezeichnung</th><th class="num">Menge</th><th>Einheit</th><th class="num">Einzelpreis</th><th class="num">Rabatt</th><th class="num">Netto</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

function totalsTable(proposal: ServiceBillingProposal): string {
  const row = (label: string, value: string, strong = false) =>
    `<tr class="${strong ? 'is-strong' : ''}"><th>${esc(label)}</th><td>${value}</td></tr>`;
  return `<table class="totals">
    ${row('Nettobetrag', money(proposal.netTotal))}
    ${row('zzgl. Umsatzsteuer', money(proposal.vatTotal))}
    ${row('Gesamtbetrag', money(proposal.grossTotal), true)}
  </table>`;
}

export interface ServiceDocumentContext {
  readonly kind: ServiceDocumentKind;
  readonly documentNumber: string;
  readonly proposal: ServiceBillingProposal;
  readonly company: TenantCompanyProfile;
  /** Ausstellungsdatum (ISO). */
  readonly issuedIso: string;
}

/** Vollständiges HTML eines Service-Belegs -- zum Anzeigen im iframe (srcDoc) und Drucken. */
export function renderServiceBillingHtml(ctx: ServiceDocumentContext): string {
  const { company, proposal, kind, documentNumber, issuedIso } = ctx;
  const accent = company.letterhead.accentColor || '#e30613';
  const label = serviceDocumentLabels[kind];
  const intro = kind === 'estimate'
    ? 'für die nachstehend beschriebenen Arbeiten unterbreiten wir Ihnen folgenden Kostenvoranschlag.'
    : 'nachstehend der geprüfte Rechnungsvorschlag zu den ausgeführten Serviceleistungen.';
  const machineLine = proposal.machineLabels.length ? proposal.machineLabels.join(', ') : '–';
  const workSection = proposal.workPerformed
    ? `<h2 class="sec">${kind === 'estimate' ? 'Vorgesehene Arbeiten' : 'Ausgeführte Arbeiten'}</h2><p class="note">${esc(proposal.workPerformed)}</p>`
    : '';
  const findingsSection = proposal.findings ? `<h2 class="sec">Weitere Befunde</h2><p class="note">${esc(proposal.findings)}</p>` : '';
  const closing = kind === 'estimate'
    ? `<p class="note">Dieser Kostenvoranschlag ist freibleibend und 30 Tage gültig. Zusätzlich anfallende Arbeiten werden nach Aufwand berechnet. Mit Ihrer Unterschrift erteilen Sie den Reparaturauftrag im o.&nbsp;g. Umfang.</p>
       <div class="sign"><div><span></span>Ort, Datum</div><div><span></span>Freigabe / Unterschrift Kunde</div></div>`
    : `<p class="note">Servicefreigabe: ${esc(proposal.preparedBy)}${proposal.customerSignatureOnPaper ? ' · Leistungsnachweis vom Kunden unterschrieben' : ''}.</p>`;
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>${esc(label)} ${esc(documentNumber)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 9mm; }
  .doc-head { min-width: 74mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 18px; font-weight: 800; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  .parties { display: flex; justify-content: space-between; gap: 12mm; margin-top: 8mm; font-size: 11.5px; }
  .parties .pl { display: block; color: #888; font-size: 9px; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 3px; }
  .intro { margin: 7mm 0 3mm; }
  h2.sec { font-size: 13px; font-weight: 700; margin: 6mm 0 2mm; }
  table.pos { width: 100%; border-collapse: collapse; margin-top: 3mm; font-size: 11px; }
  table.pos th { text-align: left; color: var(--label); font-weight: 700; padding: 6px 7px; border-bottom: 1.5px solid var(--accent); background: #fafafa; }
  table.pos td { padding: 5px 7px; border-bottom: 1px solid #e6e6e6; vertical-align: top; }
  table.pos td.c, table.pos th.c { text-align: center; width: 10mm; }
  table.pos .num { text-align: right; white-space: nowrap; }
  table.pos .postext { margin-top: 3px; font-size: 10.5px; color: #666; }
  table.totals { margin: 4mm 0 0 auto; border-collapse: collapse; min-width: 78mm; font-size: 11.5px; }
  table.totals th { text-align: left; color: #444; font-weight: 500; padding: 3px 12px 3px 0; }
  table.totals td { text-align: right; font-weight: 700; padding: 3px 0; white-space: nowrap; }
  table.totals tr.is-strong th, table.totals tr.is-strong td { border-top: 1.5px solid var(--accent); font-size: 13px; color: #111; padding-top: 5px; }
  .note { font-size: 10.5px; color: #444; margin: 4mm 0 0; text-align: justify; white-space: pre-line; }
  .sign { display: flex; gap: 14mm; margin-top: 16mm; }
  .sign div { flex: 1; font-size: 10px; color: #555; }
  .sign span { display: block; border-bottom: 1px solid #333; margin-bottom: 4px; height: 12mm; }
  .footer { margin-top: 12mm; border-top: 1.5px solid var(--accent); padding-top: 5px; display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
    table.pos thead { display: table-header-group; }
    table.pos tr, .sign, table.totals { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    ${letterhead(company)}
    <div class="band">
      <div class="sender">${esc(company.identity.name || '')}</div>
      <div class="doc-head">
        <div class="doc-title">${esc(documentHeading[kind])}</div>
        <table class="meta">
          <tr><th>Beleg-Nr.:</th><td>${esc(documentNumber)}</td></tr>
          <tr><th>Serviceauftrag:</th><td>${esc(proposal.serviceNumber)}</td></tr>
          ${proposal.orderNumber ? `<tr><th>Vorgang:</th><td>${esc(proposal.orderNumber)}</td></tr>` : ''}
          <tr><th>Datum:</th><td>${dateLabel(issuedIso)}</td></tr>
          ${proposal.customerNumber ? `<tr><th>Kunden-Nr.:</th><td>${esc(proposal.customerNumber)}</td></tr>` : ''}
        </table>
      </div>
    </div>
    <div class="parties">
      <div><span class="pl">Kunde</span>${esc(proposal.customerName)}${proposal.invoiceAddress ? `<br>${esc(proposal.invoiceAddress)}` : ''}</div>
      <div><span class="pl">Maschine / Leistungsdatum</span>${esc(machineLine)}<br>${dateLabel(proposal.serviceDate)}${proposal.technicianNames.length ? `<br>Techniker: ${esc(proposal.technicianNames.join(', '))}` : ''}</div>
    </div>
    <p class="intro">Sehr geehrte Damen und Herren,<br>${esc(intro)}</p>
    ${workSection}
    ${findingsSection}
    <h2 class="sec">Positionen</h2>
    ${positionsTable(proposal)}
    ${totalsTable(proposal)}
    ${closing}
    ${footer(company)}
  </div>
</body></html>`;
}
